import { useState } from 'react';
import { useApi, fmtAgo } from '../lib/hooks';
import { useMe } from '../lib/me';
import { api } from '../lib/api';
import { Link, navigate } from '../lib/router';
import { Icon } from '../components/Icon';
import { Alert, Avatar, Badge, Button, ErrorBox, Field, Loading, Modal, Panel, StatusBadge, Tabs, Toggle, useAction } from '../components/ui';
import { ALWAYS_ELEVATE } from '../../shared/permissions';

const REASON: Record<string, string> = { administrator: 'Administrator', allowed: 'Allowed on the channel', server: 'Server permission' };

/** The channels a Discord role opens, from Discord's permissions (Admin → Discord has the per-channel view). */
function RoleChannels({ role, publicCount, onClose }: { role: any; publicCount: number; onClose: () => void }) {
  return (
    <Modal title={`Channels for ${role.name}`} onClose={onClose}>
      <p className="muted small">Holders of <b>{role.name}</b> can open these restricted channels, plus the {publicCount} channels everyone can see. Checked for this role on its own, from the last full sync.</p>
      {role.channels.length === 0 ? <p className="muted">No restricted channels: this role sees only the public ones.</p> : (
        <ul className="role-chan-list">
          {role.channels.map((c: any) => (
            <li key={c.id}><span className="mono">#{c.name}</span><span className="muted small">{REASON[c.reason] ?? c.reason}{c.canPost ? '' : ' · read only'}</span></li>
          ))}
        </ul>
      )}
      {role.blocked?.length > 0 && (<>
        <h4 className="group-title">Blocked from</h4>
        <ul className="role-chan-list">{role.blocked.map((c: any) => <li key={c.id}><span className="mono">#{c.name}</span><span className="muted small">View Channel denied</span></li>)}</ul>
      </>)}
    </Modal>
  );
}

const hex = (n: number) => (n ? `#${n.toString(16).padStart(6, '0')}` : '#8a94a6');

export function RolesPage() {
  const { data, error, loading, reload } = useApi<any>('/api/admin/roles');
  const { can } = useMe();
  const [tab, setTab] = useState<'discord' | 'local'>('discord');
  const [holders, setHolders] = useState<{ roleRef: string; name: string } | null>(null);
  const [edit, setEdit] = useState<any | null>(null);
  const [chans, setChans] = useState<any | null>(null);
  const [folded, setFolded] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState<any | null>(null);
  const { busy, run } = useAction();
  if (loading && !data) return <Loading />;
  if (error) return <ErrorBox error={error} retry={reload} />;
  if (!data) return null;
  const manage = can('admin.roles.manage');
  // Category roles in Discord (colour #2f3136, padded name) become headings; the roles below each belong to it.
  const discordRoles = data.discord.filter((r: any) => !r.separator);
  const categoryCount = (id: string) => discordRoles.filter((r: any) => r.category === id).length;
  const fold = (id: string) => setFolded((f) => { const n = new Set(f); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  return (
    <div className="stack">
      <div className="toolbar">
        <Tabs value={tab} onChange={setTab} tabs={[{ id: 'discord', label: 'Discord roles', count: discordRoles.filter((r: any) => !r.deleted).length }, { id: 'local', label: 'Local roles', count: data.local.length }]} />
        <div className="grow" />
        {tab === 'discord' && can('admin.discord.manage') && <Button icon="refresh" busy={busy === 'sync'} onClick={() => run('sync', async () => { await api.post('/api/admin/discord/sync', { what: 'full' }); await reload(); }, 'Synced from Discord')}>Sync now</Button>}
        {tab === 'local' && manage && <Button variant="primary" icon="plus" onClick={() => setEdit({})}>New local role</Button>}
      </div>

      <Alert kind="info">
        <strong>Home page</strong> picks which roles members see as chips on their home page{data.showcaseCurated ? '' : ' (until you pick some, every Discord role is shown)'}.{' '}
        <strong>Marks</strong> tells the dashboard what a role means: a <em>combat role</em> (Berserker, Spectre, Gryphon, Strix, Sylph…) or <em>unit</em> (Ullr, Hildr…) is shown next to the member’s name;
        a <em>training</em> role marks a completed course for the Academy. Without marked roles, combat role and unit come from the member’s ORBAT seats.
      </Alert>

      {tab === 'discord' ? (
        <Panel pad={false}>
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Role</th><th>Members</th><th title="Restricted Discord channels this role opens (every role also sees the public ones)">Channels</th><th>Used in policies</th><th title="Show this role on members' home pages">Home page</th><th title="What this role means to the dashboard">Marks</th><th>Last synced</th><th /></tr></thead>
              <tbody>
                {data.discord.filter((r: any) => !r.deleted).map((r: any) => r.separator ? (
                  <tr key={r.id} className="role-cat-row">
                    <td colSpan={8}>
                      <button type="button" className="role-cat-toggle" aria-expanded={!folded.has(r.id)} onClick={() => fold(r.id)}>
                        <Icon name={folded.has(r.id) ? 'chevron' : 'down'} size={14} />{r.name}<span className="muted small">{categoryCount(r.id)} role{categoryCount(r.id) === 1 ? '' : 's'}</span>
                      </button>
                      <CategoryMark role={r} manage={manage} count={categoryCount(r.id)} onSaved={reload} />
                      {r.memberCount > 0 && <Badge tone="amber" title="Category roles are only headings in Discord; members holding one get nothing from it here">{r.memberCount} member{r.memberCount === 1 ? '' : 's'} hold this category role</Badge>}
                    </td>
                  </tr>
                ) : r.category && folded.has(r.category) ? null : (
                  <tr key={r.id} className={`${r.deleted ? 'dim' : ''} ${r.category ? 'in-role-cat' : ''}`}>
                    <td><span className="role-name"><i style={{ background: hex(r.color) }} />{r.name}</span>
                      {r.managed && <Badge>bot/integration</Badge>}{r.isEveryone && <Badge>everyone</Badge>}</td>
                    <td>{r.memberCount}</td>
                    <td>{!r.channels ? <span className="muted">—</span> : r.isEveryone ? <span className="muted small">{data.publicChannels} public</span> : (
                      <button className="link-btn" onClick={() => setChans(r)} title="Channels this role opens in Discord">
                        {r.channels.length ? `${r.channels.length} restricted` : 'public only'}{r.blocked?.length ? ` · ${r.blocked.length} blocked` : ''}
                      </button>
                    )}</td>
                    <td>{r.usedIn ? <Badge tone="blue">{r.usedIn}</Badge> : <span className="muted">—</span>}</td>
                    <DisplayCells role={r} units={data.units} manage={manage} curated={data.showcaseCurated} onSaved={reload} />
                    <td className="muted">{fmtAgo(r.lastSyncedAt)}</td>
                    <td className="right"><Button variant="ghost" onClick={() => setHolders({ roleRef: r.ref, name: r.name })}>Members</Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(() => {
            // Roles gone from Discord: kept ones are under Local roles; a previous server's are only for Remap roles.
            const gone = data.discord.filter((r: any) => r.deleted && !r.isEveryone && !r.managed);
            const kept = gone.filter((r: any) => r.keptAs).length;
            const oldUsed = gone.filter((r: any) => !r.keptAs && r.usedIn > 0).length;
            return gone.length > 0 && (
              <p className="muted small pad">
                {kept > 0 && <>{kept} role{kept === 1 ? '' : 's'} deleted in Discord {kept === 1 ? 'is' : 'are'} kept under <button className="link-btn" onClick={() => setTab('local')}>Local roles</button>. </>}
                {gone.length - kept > 0 && <>{gone.length - kept} role{gone.length - kept === 1 ? '' : 's'} from a previous Discord server {gone.length - kept === 1 ? 'is' : 'are'} hidden{oldUsed > 0 ? <>; {oldUsed} {oldUsed === 1 ? 'is' : 'are'} still used in clearance: <Link to="/admin/clearance?remap=1">Remap roles</Link></> : ' (nothing uses them)'}.</>}
              </p>
            );
          })()}
          {chans && <RoleChannels role={chans} publicCount={data.publicChannels} onClose={() => setChans(null)} />}
          <p className="muted small pad">Discord is the source of truth for these roles. Change them in Discord; they sync here every 15 minutes and at each member's login. Roles coloured #2f3136 with a padded name (like “⠀⠀Bots⠀⠀”) are treated as category headings: the roles listed below one, down to the next, belong to it.</p>
        </Panel>
      ) : (
        <Panel pad={false}>
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Role</th><th>Description</th><th>Holders</th><th>Used in policies</th><th>Home page</th><th>Marks</th><th /></tr></thead>
              <tbody>
                {data.local.map((r: any) => (
                  <tr key={r.id}>
                    <td><span className="role-name"><i style={{ background: r.color }} />{r.name}</span>{r.builtIn && <Badge tone="gold">built-in</Badge>}{r.requiresElevation && <Badge tone="amber" title="Holders must elevate each session">requires elevation</Badge>}
                      {r.convertedFrom && <Badge tone="red" title={`Deleted in Discord on ${new Date(r.convertedFrom.deletedAt).toLocaleDateString()}; kept here with its members, clearance and marks`}>deleted in Discord</Badge>}</td>
                    <td className="muted">{r.description}</td>
                    <td>{r.memberCount}</td>
                    <td>{r.usedIn ? <Badge tone="blue">{r.usedIn}</Badge> : <span className="muted">—</span>}</td>
                    <DisplayCells role={r} units={data.units} manage={manage} curated={data.showcaseCurated} onSaved={reload} />
                    <td className="right">
                      <div className="row-actions">
                        <Button variant="ghost" onClick={() => setHolders({ roleRef: r.ref, name: r.name })}>Holders</Button>
                        {manage && <Button variant="ghost" icon="edit" onClick={() => setEdit(r)} aria-label="Edit" />}
                        {manage && !r.builtIn && <Button variant="ghost" icon="trash" busy={busy === r.id} aria-label="Delete" onClick={() => setDeleting(r)} />}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted small pad">Local roles are for administration and testing. Assign them on a user's page.</p>
        </Panel>
      )}
      {holders && <HoldersModal {...holders} onClose={() => setHolders(null)} />}
      {edit && <LocalRoleModal role={edit} onClose={() => setEdit(null)} onSaved={reload} />}
      {deleting && <DeleteRoleModal role={deleting} onClose={() => setDeleting(null)} onDeleted={reload} />}
    </div>
  );
}

const KINDS: [string, string][] = [['', '—'], ['combat', 'Combat role'], ['unit', 'Unit'], ['training', 'Training / course'], ['division', 'Division']];
const KIND_LABEL: Record<string, string> = Object.fromEntries(KINDS.filter(([v]) => v));

/** A Discord category's mark: every role under it, now and later, takes it unless the role has its own. */
function CategoryMark({ role, manage, count, onSaved }: { role: any; manage: boolean; count: number; onSaved: () => void }) {
  const kind = role.display?.kind ?? '';
  const { run } = useAction();
  const save = (v: string) => run(`c-${role.ref}`, async () => {
    await api.put('/api/admin/roles/display', { ref: role.ref, kind: v || null, unit: null });
    onSaved();
  }, v ? `Every role under ${role.name} is now marked ${KIND_LABEL[v]} (unless it has its own mark)` : `${role.name} no longer marks its roles`);
  return (
    <label className="role-cat-mark" title="Marks every role in this category, including roles added to it later. A role's own mark wins.">
      <span className="muted small">Mark all {count} as</span>
      <select value={kind} disabled={!manage} aria-label={`Mark every role under ${role.name}`} onClick={(e) => e.stopPropagation()} onChange={(e) => save(e.target.value)}>
        {KINDS.map(([v, l]) => <option key={v} value={v}>{v ? l : 'No category mark'}</option>)}
      </select>
    </label>
  );
}

function DisplayCells({ role, units, manage, curated, onSaved }: { role: any; units: { slug: string; name: string }[]; manage: boolean; curated: boolean; onSaved: () => void }) {
  const d = role.display ?? {};
  const { run } = useAction();
  const save = (patch: Record<string, unknown>) => run(`d-${role.ref}`, async () => { await api.put('/api/admin/roles/display', { ref: role.ref, ...patch }); onSaved(); });
  if (role.isEveryone) return <><td /><td /></>;
  return (
    <>
      <td><Toggle checked={!!d.showcase} disabled={!manage} label={`Show ${role.name} on home pages`} onChange={(v) => save({ showcase: v })} />
        {!curated && !d.showcase && role.ref.startsWith('discord:') && <div className="muted small">shown (default)</div>}</td>
      <td>
        <div className="role-marks">
          <select value={d.kind ?? ''} disabled={!manage} aria-label={`What ${role.name} marks`} className={!d.kind && role.categoryMark ? 'inherited' : ''}
            onChange={(e) => save({ kind: e.target.value || null, ...(e.target.value !== 'unit' ? { unit: null } : {}) })}>
            {KINDS.map(([v, l]) => <option key={v} value={v}>{!v && role.categoryMark ? `${KIND_LABEL[role.categoryMark]} (from category)` : l}</option>)}
            {role.categoryMark && <option value="none">No mark (ignore category)</option>}
          </select>
          {d.kind === 'unit' && (
            <select value={d.unit ?? ''} disabled={!manage} aria-label="ORBAT unit" onChange={(e) => save({ unit: e.target.value || null })}>
              <option value="">Match by name</option>
              {units.map((u) => <option key={u.slug} value={u.slug}>{u.name}</option>)}
            </select>
          )}
        </div>
      </td>
    </>
  );
}

function HoldersModal({ roleRef, name, onClose }: { roleRef: string; name: string; onClose: () => void }) {
  const { data, loading } = useApi<any[]>(`/api/admin/roles/holders?ref=${encodeURIComponent(roleRef)}`);
  return (
    <Modal title={`Members with ${name}`} onClose={onClose}>
      {loading ? <Loading /> : data?.length ? (
        <ul className="holder-list">{data.map((u) => <li key={u.id}><Avatar src={u.avatarUrl} name={u.displayName} size={26} /><Link to={`/admin/users/${u.id}`} onClick={onClose}>{u.displayName}</Link><StatusBadge status={u.status} /></li>)}</ul>
      ) : <p className="muted">No one holds this role.</p>}
    </Modal>
  );
}

function LocalRoleModal({ role, onClose, onSaved }: { role: any; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(role.name ?? '');
  const [description, setDescription] = useState(role.description ?? '');
  const [color, setColor] = useState(role.color ?? '#7aa2d6');
  const [requiresElevation, setRequiresElevation] = useState<boolean>(!!role.requiresElevation);
  const { busy, run } = useAction();
  const save = () => run('save', async () => {
    if (role.id) await api.patch(`/api/admin/roles/local/${role.id}`, { name, description, color, requiresElevation });
    else await api.post('/api/admin/roles/local', { name, description, color, requiresElevation });
    onSaved(); onClose();
  }, 'Role saved');
  return (
    <Modal title={role.id ? `Edit ${role.name}` : 'New local role'} onClose={onClose}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy === 'save'} onClick={save}>Save</Button></>}>
      <Field label="Name"><input value={name} disabled={role.builtIn} onChange={(e) => setName(e.target.value)} autoFocus /></Field>
      <Field label="Description"><input value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
      <Field label="Colour"><input type="color" value={color} onChange={(e) => setColor(e.target.value)} /></Field>
      <label className="check-row">
        <input type="checkbox" checked={requiresElevation} disabled={ALWAYS_ELEVATE.includes(role.id)} onChange={(e) => setRequiresElevation(e.target.checked)} />
        <strong>Requires elevation</strong>
        <span className="muted small">Holders must choose “Elevate role” each session before this role takes effect.{ALWAYS_ELEVATE.includes(role.id) ? ` Always on for ${role.name}.` : ''}</span>
      </label>
    </Modal>
  );
}

/** Delete a local role, warning first when clearance, marks or tools still use it (remap those first). */
function DeleteRoleModal({ role, onClose, onDeleted }: { role: any; onClose: () => void; onDeleted: () => void }) {
  const { data, loading, error } = useApi<{ policies: { node: string; label: string; how: string }[]; toolRefs: number; mark: string | null; holders: number; inUse: boolean }>(`/api/admin/roles/local/${role.id}/usage`);
  const { busy, run } = useAction();
  const del = () => run('del', async () => { await api.del(`/api/admin/roles/local/${role.id}`); onDeleted(); onClose(); }, 'Role deleted');
  return (
    <Modal title={`Delete “${role.name}”?`} onClose={onClose}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        {data?.inUse && role.convertedFrom && <Button icon="remap" onClick={() => navigate('/admin/clearance?remap=1')}>Open Remap roles</Button>}
        <Button variant="danger" icon="trash" busy={busy === 'del'} disabled={!data} onClick={del}>{data?.inUse ? 'Delete anyway' : 'Delete role'}</Button>
      </>}>
      {loading ? <Loading /> : error ? <ErrorBox error={error} /> : data && (
        <div className="stack-sm">
          {data.inUse ? (
            <Alert kind="warn">
              This role is still in use. Deleting it removes it from everything below, so members who got access through it lose that access.
              {role.convertedFrom ? <>Use <b>Remap roles</b> first to point these at the role that replaces it.</> : <>Give the replacement role these clearances first.</>}
            </Alert>
          ) : <p>Nothing uses this role any more, so it’s safe to delete.</p>}
          {data.policies.length > 0 && (
            <div>
              <strong className="small">Clearance ({data.policies.length})</strong>
              <ul className="small role-usage">{data.policies.map((p) => <li key={p.node}>{p.label} <span className="muted">({p.how === 'deny' ? 'denied' : p.how === 'require' ? 'required' : 'allowed'})</span></li>)}</ul>
            </div>
          )}
          {data.mark && <p className="small"><strong>Mark:</strong> {KIND_LABEL[data.mark] ?? data.mark}</p>}
          {data.toolRefs > 0 && <p className="small"><strong>Tool settings:</strong> {data.toolRefs} (for example who can see a link)</p>}
          <p className="muted small">{data.holders} member{data.holders === 1 ? '' : 's'} hold{data.holders === 1 ? 's' : ''} this role; it’s taken off them.</p>
        </div>
      )}
    </Modal>
  );
}
